import { useQuery } from '@tanstack/react-query';

import {
  api,
  type BondAuthorizedContactWireItem,
  type BondParticipantWireItem,
  type BondRelatedPersonWireItem,
  type BondWireItem,
} from '../lib/api-client';

/**
 * useBond / useBondParticipants / useBondRelatedPersons /
 * useBondAuthorizedContacts — Fatia B + B-bis (ADR-037 / 129 / 130 / 131).
 *
 * Hooks read-only que consomem `GET /v1/bonds/:bondId` + 3 sub-recursos
 * ortogonais (participants / related-persons / authorized-contacts).
 * Capability gate `bond-basic` aplicado no chamador (TabCliente) — defense
 * in depth: backend também valida via `@RequiresCapability('bond-basic')`.
 *
 * Em 403 o `api-client.callJson` dispara `onForbidden` (toast) e preserva
 * sessão (Item 88 — PR #43). O hook expõe `isError` para o componente
 * renderizar empty/disabled state quando preferir.
 *
 * Cache: staleTime 60s — Bond muda raramente durante uma conversa.
 */
export function useBond(bondId: string | null | undefined): {
  data: BondWireItem | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
} {
  const id = bondId ?? undefined;
  const query = useQuery({
    queryKey: ['bond', id],
    queryFn: () => {
      if (id === undefined) throw new Error('bondId required');
      return api.getBond(id);
    },
    enabled: id !== undefined,
    staleTime: 60 * 1000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

export function useBondParticipants(bondId: string | null | undefined): {
  data: BondParticipantWireItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const id = bondId ?? undefined;
  const query = useQuery({
    queryKey: ['bond-participants', id],
    queryFn: async () => {
      if (id === undefined) throw new Error('bondId required');
      const res = await api.listBondParticipants(id);
      return res.data;
    },
    enabled: id !== undefined,
    staleTime: 60 * 1000,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useBondRelatedPersons(bondId: string | null | undefined): {
  data: BondRelatedPersonWireItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const id = bondId ?? undefined;
  const query = useQuery({
    queryKey: ['bond-related-persons', id],
    queryFn: async () => {
      if (id === undefined) throw new Error('bondId required');
      const res = await api.listBondRelatedPersons(id);
      return res.data;
    },
    enabled: id !== undefined,
    staleTime: 60 * 1000,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useBondAuthorizedContacts(bondId: string | null | undefined): {
  data: BondAuthorizedContactWireItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const id = bondId ?? undefined;
  const query = useQuery({
    queryKey: ['bond-authorized-contacts', id],
    queryFn: async () => {
      if (id === undefined) throw new Error('bondId required');
      const res = await api.listBondAuthorizedContacts(id);
      return res.data;
    },
    enabled: id !== undefined,
    staleTime: 60 * 1000,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
